/**
 * Outil « Ensembles de nombres » — données et règles (pur).
 *
 * ℕ ⊂ ℤ ⊂ 𝔻 ⊂ ℚ ⊂ ℝ. Chaque nombre a son PLUS PETIT ensemble ; il appartient
 * à celui-ci et à tous ceux qui le contiennent. Les pièges : √9 = 3 est
 * naturel, 10/5 = 2 aussi, −1/4 = −0,25 est décimal, 1/3 ne l'est pas,
 * 22/7 est rationnel (ce n'est pas π), 3,14 est décimal (ce n'est pas π).
 */

export const SETS = ['N', 'Z', 'D', 'Q', 'R'] as const;
export type SetId = (typeof SETS)[number];

export const SET_INFO: Record<SetId, { symbol: string; tex: string; name: string }> = {
  N: { symbol: 'ℕ', tex: '\\mathbb{N}', name: 'entiers naturels' },
  Z: { symbol: 'ℤ', tex: '\\mathbb{Z}', name: 'entiers relatifs' },
  D: { symbol: '𝔻', tex: '\\mathbb{D}', name: 'décimaux' },
  Q: { symbol: 'ℚ', tex: '\\mathbb{Q}', name: 'rationnels' },
  R: { symbol: 'ℝ', tex: '\\mathbb{R}', name: 'réels' },
};

export interface NumberCard {
  id: string;
  /** écriture LaTeX du nombre */
  tex: string;
  /** écriture pour les messages */
  text: string;
  /** lecture à voix haute (« moins 1 sur 4 ») */
  spoken: string;
  smallest: SetId;
  /** pourquoi c'est son plus petit ensemble */
  why: string;
}

export const NUMBERS: readonly NumberCard[] = [
  {
    id: '7',
    spoken: '7',
    tex: '7',
    text: '7',
    smallest: 'N',
    why: '7 est un entier positif : un entier naturel.',
  },
  {
    id: 'm3',
    spoken: 'moins 3',
    tex: '-3',
    text: '−3',
    smallest: 'Z',
    why: '−3 est un entier négatif : relatif, pas naturel.',
  },
  {
    id: 'sqrt9',
    spoken: 'racine de 9',
    tex: '\\sqrt{9}',
    text: '√9',
    smallest: 'N',
    why: '√9 = 3 : c’est un entier naturel déguisé.',
  },
  {
    id: '0,25',
    spoken: '0,25',
    tex: '0{,}25',
    text: '0,25',
    smallest: 'D',
    why: '0,25 = 25/100 : une écriture décimale qui s’arrête.',
  },
  {
    id: '2/3',
    spoken: '2 sur 3',
    tex: '\\frac{2}{3}',
    text: '2/3',
    smallest: 'Q',
    why: '2 ÷ 3 = 0,666… ne s’arrête jamais : 2/3 est rationnel, pas décimal.',
  },
  {
    id: 'sqrt2',
    spoken: 'racine de 2',
    tex: '\\sqrt{2}',
    text: '√2',
    smallest: 'R',
    why: '√2 n’est le quotient d’aucun couple d’entiers : il est irrationnel.',
  },
  {
    id: 'pi',
    spoken: 'pi',
    tex: '\\pi',
    text: 'π',
    smallest: 'R',
    why: 'π est irrationnel : 3,14 et 22/7 n’en sont que des valeurs approchées.',
  },
  {
    id: '10/5',
    spoken: '10 sur 5',
    tex: '\\frac{10}{5}',
    text: '10/5',
    smallest: 'N',
    why: '10/5 = 2 : c’est un entier naturel.',
  },
  {
    id: 'm1/4',
    spoken: 'moins 1 sur 4',
    tex: '-\\frac{1}{4}',
    text: '−1/4',
    smallest: 'D',
    why: '−1/4 = −0,25 : un décimal négatif.',
  },
  {
    id: '1/3',
    spoken: '1 sur 3',
    tex: '\\frac{1}{3}',
    text: '1/3',
    smallest: 'Q',
    why: '1 ÷ 3 = 0,333… ne s’arrête jamais : 1/3 n’est pas décimal.',
  },
  {
    id: 'm12/4',
    spoken: 'moins 12 sur 4',
    tex: '-\\frac{12}{4}',
    text: '−12/4',
    smallest: 'Z',
    why: '−12/4 = −3 : un entier relatif.',
  },
  {
    id: '22/7',
    spoken: '22 sur 7',
    tex: '\\frac{22}{7}',
    text: '22/7',
    smallest: 'Q',
    why: '22/7 est un quotient d’entiers : rationnel (et ce n’est pas π, seulement proche).',
  },
  {
    id: '3,14',
    spoken: '3,14',
    tex: '3{,}14',
    text: '3,14',
    smallest: 'D',
    why: '3,14 = 314/100 est décimal ; ce n’est pas π.',
  },
  { id: '0', spoken: '0', tex: '0', text: '0', smallest: 'N', why: '0 est un entier naturel.' },
  {
    id: 'msqrt4',
    spoken: 'moins racine de 4',
    tex: '-\\sqrt{4}',
    text: '−√4',
    smallest: 'Z',
    why: '−√4 = −2 : un entier relatif.',
  },
  {
    id: '7/20',
    spoken: '7 sur 20',
    tex: '\\frac{7}{20}',
    text: '7/20',
    smallest: 'D',
    why: '7/20 = 35/100 = 0,35 : décimal.',
  },
];

const rank = (s: SetId) => SETS.indexOf(s);

/** le nombre appartient-il à l'ensemble ? (son plus petit ensemble est inclus dedans) */
export function belongs(n: NumberCard, s: SetId): boolean {
  return rank(n.smallest) <= rank(s);
}

export type Verdict = 'exact' | 'tooBig' | 'tooSmall';

/** l'élève range `n` dans `s` : le bon plus petit ensemble, un trop grand (vrai mais pas le plus petit), ou faux */
export function judge(n: NumberCard, s: SetId): Verdict {
  if (s === n.smallest) return 'exact';
  return belongs(n, s) ? 'tooBig' : 'tooSmall';
}

/** « ℕ ⊂ ℤ ⊂ 𝔻 ⊂ ℚ ⊂ ℝ » à partir de `s` : les ensembles qui contiennent le nombre */
export function containing(s: SetId): SetId[] {
  return SETS.slice(rank(s));
}
